import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  CalendarCheck, Loader2, CheckCircle2, FileDown, X, ChevronLeft, Clock,
  Car, PawPrint, Hourglass, Sparkles,
} from "lucide-react";
import { api, FORMULES } from "@/lib/api";

const FORMULE_OPTIONS = FORMULES.map((f) => ({
  key: f.key,
  name: f.name,
  price: f.price,
  label: `${f.name} — ${f.price}`,
  price_value: parseInt(f.price.replace(/[^\d]/g, ""), 10) || 0,
  pdf: f.pdf,
}));

const OPTION_PRICE = 10;
const OPTION_ITEMS = [
  { key: "suv", label: "SUV", Icon: Car },
  { key: "animal", label: "Poils d'animaux", Icon: PawPrint },
  { key: "sable", label: "Sable", Icon: Hourglass },
  { key: "tres-sale", label: "Véhicule très sale", Icon: Sparkles },
];

function ymd(d) {
  // local YYYY-MM-DD
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function timeOf(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function frenchDate(d) {
  return d.toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export default function BookingDialog({ open, onOpenChange, defaultFormule = "" }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle: "", address: "",
    formule: defaultFormule || "", options: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [availableDateSet, setAvailableDateSet] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    // reset
    setStep(1);
    setDate(null);
    setSelectedSlot(null);
    setSlots([]);
    setSubmitted(false);
    setForm({
      name: "", phone: "", email: "", vehicle: "", address: "",
      formule: defaultFormule || "", options: [], message: "",
    });
    // pre-fetch slots in next 60 days to highlight available dates
    const now = new Date();
    const to = new Date(now);
    to.setDate(to.getDate() + 60);
    api
      .get(`/slots?from_date=${ymd(now)}&to_date=${ymd(to)}`)
      .then((r) => {
        const set = new Set();
        (r.data || []).forEach((s) => set.add(s.start.slice(0, 10)));
        setAvailableDateSet(set);
      })
      .catch(() => setAvailableDateSet(new Set()));
  }, [open, defaultFormule]);

  // when date selected, fetch slots for that day
  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    const dateStr = ymd(date);
    api
      .get(`/slots?from_date=${dateStr}&to_date=${dateStr}`)
      .then((r) => setSlots(r.data || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleOption = (key) =>
    setForm((f) => ({
      ...f,
      options: f.options.includes(key)
        ? f.options.filter((o) => o !== key)
        : [...f.options, key],
    }));

  const formuleValue = (FORMULE_OPTIONS.find((o) => o.key === form.formule) || {}).price_value || 0;
  const optionsValue = form.options.length * OPTION_PRICE;
  const totalValue = formuleValue + optionsValue;

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return toast.error("Sélectionnez un créneau.");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      return toast.error("Nom, téléphone et email sont obligatoires.");
    }
    if (!form.address.trim()) return toast.error("Indiquez l'adresse d'intervention.");
    if (!form.formule) return toast.error("Choisissez une formule.");
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        vehicle: form.vehicle,
        address: form.address,
        formule: form.formule,
        options: form.options,
        slot_id: selectedSlot.id,
        message: form.message,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Créneau déjà réservé") {
        toast.error("Ce créneau vient d'être réservé. Choisissez-en un autre.");
        setStep(2);
        setSelectedSlot(null);
      } else {
        toast.error("Une erreur est survenue. Réessayez ou appelez directement.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const chosenFormule = FORMULE_OPTIONS.find((o) => o.key === form.formule);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl"
      >
        {submitted ? (
          <SuccessPanel formule={chosenFormule} onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#0B3A5B] flex items-center gap-2">
                <CalendarCheck className="h-5 w-5 text-[#1E88C8]" />
                Prendre rendez-vous
              </DialogTitle>
              <DialogDescription>
                {step === 1 && "Choisissez une date disponible."}
                {step === 2 && date && `Créneaux du ${frenchDate(date)}.`}
                {step === 3 && "Vos informations et la formule souhaitée."}
              </DialogDescription>
              <Stepper step={step} />
            </DialogHeader>

            {/* STEP 1 — Date */}
            {step === 1 && (
              <div className="mt-2" data-testid="booking-step-1">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      if (!d) return;
                      setDate(d);
                      setStep(2);
                    }}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (d < today) return true;
                      return !availableDateSet.has(ymd(d));
                    }}
                    modifiers={{
                      available: (d) => availableDateSet.has(ymd(d)),
                    }}
                    modifiersClassNames={{
                      available: "ring-1 ring-[#1E88C8]/40 rounded-md",
                    }}
                    className="rounded-2xl border border-[#0B3A5B]/10"
                  />
                </div>
                <p className="mt-4 text-xs text-center text-[#2F3438]/60">
                  Seules les dates avec des créneaux disponibles sont sélectionnables.
                </p>
              </div>
            )}

            {/* STEP 2 — Slot */}
            {step === 2 && date && (
              <div className="mt-2 space-y-4" data-testid="booking-step-2">
                <button
                  type="button"
                  data-testid="booking-back-to-date"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1 text-sm text-[#0B3A5B]/70 hover:text-[#0B3A5B]"
                >
                  <ChevronLeft className="h-4 w-4" /> Changer de date
                </button>
                {loadingSlots ? (
                  <div className="py-10 text-center text-sm text-[#2F3438]/60">
                    <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                    Chargement…
                  </div>
                ) : slots.length === 0 ? (
                  <div className="py-10 text-center text-sm text-[#2F3438]/60">
                    Aucun créneau disponible ce jour-là.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {slots.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`slot-${s.id}`}
                        onClick={() => { setSelectedSlot(s); setStep(3); }}
                        className="rounded-xl border border-[#0B3A5B]/15 hover:border-[#1E88C8] hover:bg-[#1E88C8]/5 py-3 px-4 text-left transition"
                      >
                        <div className="flex items-center gap-2 text-[#0B3A5B] font-semibold">
                          <Clock className="h-4 w-4 text-[#1E88C8]" />
                          {timeOf(s.start)}
                        </div>
                        <div className="text-xs text-[#2F3438]/60 mt-0.5">
                          jusqu&apos;à {timeOf(s.end)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — Form */}
            {step === 3 && selectedSlot && (
              <form onSubmit={submit} className="mt-2 space-y-4" data-testid="booking-step-3">
                <button
                  type="button"
                  data-testid="booking-back-to-slot"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-1 text-sm text-[#0B3A5B]/70 hover:text-[#0B3A5B]"
                >
                  <ChevronLeft className="h-4 w-4" /> Changer de créneau
                </button>

                <div className="rounded-xl bg-[#F3F5F7] p-3 text-sm flex items-center gap-2 text-[#0B3A5B]">
                  <CalendarCheck className="h-4 w-4 text-[#1E88C8]" />
                  <span className="font-semibold">
                    {frenchDate(date)} · {timeOf(selectedSlot.start)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field id="b-name" label="Nom / Prénom *">
                    <Input data-testid="booking-name" id="b-name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                  </Field>
                  <Field id="b-phone" label="Téléphone *">
                    <Input data-testid="booking-phone" id="b-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} required />
                  </Field>
                  <Field id="b-email" label="Email *">
                    <Input data-testid="booking-email" id="b-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
                  </Field>
                  <Field id="b-vehicle" label="Véhicule">
                    <Input data-testid="booking-vehicle" id="b-vehicle" placeholder="Ex : Peugeot 3008" value={form.vehicle} onChange={(e) => update("vehicle", e.target.value)} />
                  </Field>
                </div>

                <Field id="b-address" label="📍 Adresse d'intervention *">
                  <Textarea
                    data-testid="booking-address"
                    id="b-address"
                    rows={2}
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="Numéro et rue · Code postal · Ville"
                    required
                  />
                  <p className="text-xs text-[#2F3438]/60 mt-1">
                    Indiquez l&apos;adresse où le véhicule devra être lavé (domicile, entreprise ou autre lieu d&apos;intervention).
                  </p>
                </Field>

                <Field id="b-formule" label="Formule souhaitée *">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {FORMULE_OPTIONS.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`booking-formule-${o.key}`}
                        onClick={() => update("formule", o.key)}
                        className={`rounded-xl border p-3 text-left text-sm transition ${
                          form.formule === o.key
                            ? "border-[#1E88C8] bg-[#1E88C8]/5"
                            : "border-[#0B3A5B]/15 hover:border-[#0B3A5B]/30"
                        }`}
                      >
                        <div className="font-display text-[#0B3A5B] whitespace-nowrap flex items-baseline gap-1.5">
                          <span className="truncate">{o.name}</span>
                          <span className="text-[#1E88C8] font-bold shrink-0">{o.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Options supplémentaires (+10 € par option)">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {OPTION_ITEMS.map(({ key, label, Icon }) => {
                      const checked = form.options.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`booking-option-${key}`}
                          onClick={() => toggleOption(key)}
                          className={`rounded-xl border p-3 text-left transition ${
                            checked
                              ? "border-[#1E88C8] bg-[#1E88C8]/5"
                              : "border-[#0B3A5B]/15 hover:border-[#0B3A5B]/30"
                          }`}
                        >
                          <Icon className={`h-5 w-5 mb-1.5 ${checked ? "text-[#1E88C8]" : "text-[#2F3438]/60"}`} />
                          <div className="text-xs font-semibold text-[#0B3A5B] leading-tight">
                            {label}
                          </div>
                          <div className="text-[10px] text-[#2F3438]/60 mt-0.5">+10 €</div>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {/* Total automatique */}
                {form.formule && (
                  <div
                    data-testid="booking-total"
                    className="rounded-xl border border-[#0B3A5B]/15 bg-[#F3F5F7] p-4 space-y-1.5 text-sm"
                  >
                    <div className="flex justify-between text-[#2F3438]/80">
                      <span>
                        {(FORMULE_OPTIONS.find((o) => o.key === form.formule) || {}).label}
                      </span>
                      <span>{formuleValue},00 €</span>
                    </div>
                    {form.options.map((opt) => {
                      const item = OPTION_ITEMS.find((i) => i.key === opt);
                      return (
                        <div key={opt} className="flex justify-between text-[#2F3438]/80">
                          <span>+ {item?.label}</span>
                          <span>{OPTION_PRICE},00 €</span>
                        </div>
                      );
                    })}
                    <div className="flex justify-between pt-2 border-t border-[#0B3A5B]/15 font-display text-lg text-[#0B3A5B]">
                      <span>Total</span>
                      <span className="text-[#1E88C8] font-bold" data-testid="booking-total-value">
                        {totalValue},00 €
                      </span>
                    </div>
                  </div>
                )}

                <Field id="b-msg" label="Message (optionnel)">
                  <Textarea data-testid="booking-message" id="b-msg" rows={2} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Précisions, lieu d'intervention…" />
                </Field>

                <Button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Envoyer la demande
                </Button>
                <p className="text-xs text-[#2F3438]/55 text-center">
                  Réponse sous 48h. Le créneau est provisoirement réservé.
                </p>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ step }) {
  const items = ["Date", "Créneau", "Vos infos"];
  return (
    <div className="flex items-center justify-center gap-2 pt-3">
      {items.map((label, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <div key={label} className="flex items-center gap-2">
            <span
              className={`h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center ${
                active
                  ? "bg-[#1E88C8] text-white"
                  : done
                  ? "bg-[#0B3A5B] text-white"
                  : "bg-[#F3F5F7] text-[#0B3A5B]/50"
              }`}
            >
              {idx}
            </span>
            <span className={`text-xs ${active ? "text-[#0B3A5B] font-semibold" : "text-[#2F3438]/60"}`}>
              {label}
            </span>
            {i < items.length - 1 && <span className="text-[#2F3438]/30 mx-1">›</span>}
          </div>
        );
      })}
    </div>
  );
}

function SuccessPanel({ formule, onClose }) {
  const hasPdf = formule && formule.pdf;
  return (
    <div data-testid="booking-success" className="py-4">
      <div className="flex items-center justify-center">
        <span className="h-16 w-16 rounded-full bg-[#1E88C8]/10 text-[#1E88C8] flex items-center justify-center">
          <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
        </span>
      </div>
      <DialogTitle className="mt-5 text-center font-display text-2xl text-[#0B3A5B]">
        Demande enregistrée
      </DialogTitle>
      <DialogDescription className="mt-3 text-center text-[#2F3438]/80 leading-relaxed">
        Votre demande a bien été enregistrée. Le créneau demandé est provisoirement réservé.
        Une confirmation ou un refus vous sera communiqué sous 48 heures. Passé ce délai sans
        validation, la demande sera automatiquement annulée. Le devis détaillé vient de vous
        être envoyé par email.
      </DialogDescription>

      {hasPdf && (
        <a
          href={formule.pdf}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="booking-pdf-link"
          className="btn-press mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#0B3A5B] hover:bg-[#0a3251] text-white font-semibold transition-all"
        >
          <FileDown className="h-4 w-4" />
          Télécharger le devis
        </a>
      )}

      <Button
        onClick={onClose}
        data-testid="booking-success-close"
        variant="ghost"
        className="mt-3 w-full h-11 rounded-full text-[#2F3438]/70 hover:text-[#0B3A5B]"
      >
        <X className="h-4 w-4 mr-2" />
        Fermer
      </Button>
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-[#2F3438]/70 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
